/**
 * Authentication context.
 *
 * The access token is held in the API client's module scope (not React state,
 * not localStorage). This context tracks only the *derived* session facts the
 * UI needs: who is signed in, which workspace is active, and what they may do.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { ApiError, bootstrapSession, setActiveOrganization, setSession } from '@/api/client';
import { authApi } from '@/api/endpoints';
import type { MeResponse, OrganizationSummary, UserProfile } from '@/types/api';

interface AuthState {
  status: 'loading' | 'authenticated' | 'anonymous';
  user: UserProfile | null;
  organizations: OrganizationSummary[];
  activeOrganization: OrganizationSummary | null;
  permissions: Set<string>;
  can: (permission: string) => boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (input: {
    email: string;
    password: string;
    full_name: string;
    organization_name?: string;
  }) => Promise<boolean>;
  logout: () => Promise<void>;
  switchOrganization: (organizationId: string) => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<AuthState['status']>('loading');
  const [me, setMe] = useState<MeResponse | null>(null);

  const loadProfile = useCallback(async () => {
    try {
      const profile = await authApi.me();
      setMe(profile);
      setStatus('authenticated');
    } catch {
      setMe(null);
      setStatus('anonymous');
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    // On load the access token is gone (memory only) but the refresh cookie may
    // still be valid, so try to restore the session before deciding.
    void (async () => {
      const restored = await bootstrapSession();
      if (cancelled) return;
      if (restored) {
        await loadProfile();
      } else {
        setStatus('anonymous');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadProfile]);

  const login = useCallback(
    async (email: string, password: string) => {
      const session = await authApi.login({ email, password });
      if (session.user.status === 'pending_verification') {
        throw new ApiError(403, { error: { code: 'email_not_verified', message: 'Your account awaits identity verification. Contact the platform administrator.' } }, 'Identity verification required.');
      }
      setSession(session);
      setActiveOrganization(session.active_organization_id);
      await loadProfile();
      await queryClient.invalidateQueries();
    },
    [loadProfile, queryClient],
  );

  const register = useCallback(
    async (input: {
      email: string;
      password: string;
      full_name: string;
      organization_name?: string;
    }) => {
      const session = await authApi.register(input);
      if (session.user.status === 'pending_verification') {
        setSession(null);
        setMe(null);
        setStatus('anonymous');
        return false;
      }
      setSession(session);
      setActiveOrganization(session.active_organization_id);
      await loadProfile();
      return true;
    },
    [loadProfile],
  );

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      setSession(null);
      setActiveOrganization(null);
      setMe(null);
      setStatus('anonymous');
      queryClient.clear();
    }
  }, [queryClient]);

  const switchOrganization = useCallback(
    async (organizationId: string) => {
      setActiveOrganization(organizationId);
      // Every cached query is workspace-scoped, so none of it survives a switch.
      queryClient.clear();
      await loadProfile();
    },
    [loadProfile, queryClient],
  );

  const value = useMemo<AuthState>(() => {
    const permissions = new Set(me?.permissions ?? []);
    return {
      status,
      user: me?.user ?? null,
      organizations: me?.organizations ?? [],
      activeOrganization: me?.active_organization ?? null,
      permissions,
      can: (permission: string) => permissions.has(permission),
      login,
      register,
      logout,
      switchOrganization,
      refresh: loadProfile,
    };
  }, [me, status, login, register, logout, switchOrganization, loadProfile]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}
